import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import { useWindowSize } from '../../../core/utils/Hooks';
import { colors, text } from '../../../core/styles/theme/default';
import months from '../../../core/i18n/months';
import i18n from './electricVehicleChart-i18n';
import { H2, IconSVG } from '../../../primitives';
import { Grid, GridItem, Spacer } from '../../../helpers';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const StyledTitle = styled(H2)`
  text-align: center;
`;

const StyledNavWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledYearsNavWrapper = styled(StyledNavWrapper)`
  justify-content: flex-end;
  margin-right: ${rem(-8)};
`;

const StyledNavButton = styled.button(
  ({ isPrev, disabled }: { isPrev?: boolean; disabled?: boolean }) => css`
    border: ${rem(1)} solid ${colors.blue800};
    padding: 0;
    background: none;
    margin: 0 ${rem(8)};
    cursor: pointer;
    position: relative;
    background: transparent;
    border-radius: 50%;
    width: ${rem(44)};
    height: ${rem(44)};

    svg {
      vertical-align: middle;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-40%, -46%);

      path {
        stroke: ${colors.blue800};
      }
    }

    ${disabled
      ? css`
          opacity: 0.2;
          cursor: default;
        `
      : css`
          &:hover,
          &:focus {
            background-color: ${colors.blue800};
            svg path {
              stroke: ${colors.blue100};
            }
          }
        `}

    ${isPrev &&
    css`
      transform: scaleX(-1);
    `}
  `,
);

const StyledYear = styled.span`
  ${text.h3}
  margin: 0 ${rem(8)};
`;

const StyledSummary = styled.div`
  ${text.h3}
  text-align: center;
`;

const roundValue = (value: number) => Math.round(value * 10) / 10;

const StyledTooltip = styled.div`
  background: rgba(0, 0, 0, 0.8);
  color: ${colors.white};
  padding: ${rem(8)};
  border-radius: ${rem(6)};
  font-size: ${rem(12)};
  line-height: 1;

  header {
    margin-bottom: ${rem(10)};
  }

  p {
    margin: 0;
    display: flex;
    align-items: center;

    + p {
      padding-top: ${rem(6)};
    }

    span {
      border: ${rem(1)} solid ${colors.white};
      height: ${rem(12)};
      width: ${rem(12)};
      margin-right: ${rem(4)};
    }
  }
`;

interface CustomTooltipProps {
  active: boolean;
  payload: { value: number; dataKey: string; fill: string }[];
  label: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (
    !active ||
    !payload?.length ||
    payload.every((item) => item.value === 0)
  ) {
    return null;
  }

  return (
    <StyledTooltip>
      <header>
        <strong>{label}</strong>
      </header>
      {payload
        .slice()
        .reverse()
        .map((item, index) => (
          <p key={index}>
            <span style={{ background: item.fill }} />
            {item.dataKey.toUpperCase()} {item.value}%
          </p>
        ))}
    </StyledTooltip>
  );
};

const ElectricVehicleChart = ({ locale, data }: ElectricVehicleChartProps) => {
  const years = Object.keys(data).map(Number).sort();
  const [currentYear, setCurrentYear] = useState(years[years.length - 1]);
  const [colsCount, setColsCount] = useState(12);
  const [lastVisibleCol, setLastVisibleCol] = useState(colsCount);
  const [yAxisMax] = useState(() => {
    const maxSum = Math.max(
      ...Object.values(data).flatMap(({ bev, phev }) =>
        bev.map((bevValue, index) => bevValue + phev[index]),
      ),
    );
    return Math.ceil(maxSum / 10) * 10;
  });
  const { width: windowWidth } = useWindowSize();
  const yearData = data[currentYear];
  const bevColor = '#acd373';
  const phevColor = '#1cbbb4';

  const sumArray = Array(12)
    .fill(0)
    .map((item: number, index) => {
      item += (yearData.bev[index] ?? 0) + (yearData.phev[index] ?? 0);

      return roundValue(item);
    })
    .filter(Boolean);
  const totalBev =
    yearData.totalBev ??
    roundValue(
      yearData.bev.reduce((acc, val) => acc + val, 0) / sumArray.length,
    );
  const totalPhev =
    yearData.totalPhev ??
    roundValue(
      yearData.phev.reduce((acc, val) => acc + val, 0) / sumArray.length,
    );
  const total = roundValue(totalBev + totalPhev);
  const chartData = months.short[locale]
    .map((label, index) => ({
      label: `${label} ` + (sumArray[index] ? `${sumArray[index]}%` : ''),
      bev: yearData.bev[index],
      phev: yearData.phev[index],
    }))
    .slice(lastVisibleCol - colsCount, lastVisibleCol);

  useEffect(() => {
    if (!windowWidth) return;

    let cols = 12;

    if (windowWidth >= 768) {
      cols = 12;
    } else if (windowWidth >= 480) {
      cols = 8;
    } else {
      cols = 4;
    }

    setColsCount(cols);
  }, [windowWidth]);

  useEffect(() => {
    setLastVisibleCol(colsCount);
  }, [colsCount]);

  return (
    <div>
      <Grid columns={12}>
        <GridItem
          size={{ zero: 12, medium: 10, large: 8 }}
          push={{ zero: 0, medium: 1, large: 2 }}
        >
          <StyledTitle
            dangerouslySetInnerHTML={{
              __html: i18n.heading[locale]
                .replace(
                  'BEV',
                  `<span style="color: ${bevColor}">Battery Electric Vehicle</span>`,
                )
                .replace(
                  'PHEV',
                  `<span style="color: ${phevColor}">Plug-In Hybrid Electric Vehicle</span>`,
                ),
            }}
          />
        </GridItem>
      </Grid>
      <Spacer space="beta" />
      <StyledYearsNavWrapper>
        <StyledNavButton
          aria-label="Previous year"
          disabled={currentYear === years[0]}
          onClick={() => setCurrentYear((prev) => prev - 1)}
          isPrev
        >
          <IconSVG iconSize="20" iconName="FmdsIconChevronRight" />
        </StyledNavButton>
        <StyledYear>{currentYear}</StyledYear>
        <StyledNavButton
          aria-label="Next year"
          disabled={currentYear === years[years.length - 1]}
          onClick={() => setCurrentYear((prev) => prev + 1)}
        >
          <IconSVG iconSize="20" iconName="FmdsIconChevronRight" />
        </StyledNavButton>
      </StyledYearsNavWrapper>
      <ResponsiveContainer aspect={16 / 9}>
        <BarChart
          data={chartData}
          margin={{ top: 12, right: 0, bottom: 12, left: 0 }}
        >
          <CartesianGrid strokeDasharray="4 4" />
          <YAxis
            style={{
              fontSize: 12,
            }}
            width={40}
            interval={0}
            tickFormatter={(value) => value + '%'}
            domain={[0, yAxisMax]}
          />
          <XAxis
            dataKey="label"
            style={{
              fontSize: 12,
            }}
            height={40}
            interval={0}
            tick={({ x, y, payload }) => (
              <g
                transform={`translate(${x},${y + 12})`}
                style={{ fontSize: 12, fill: '#666' }}
              >
                {payload.value.split(' ').map((line: string, i: number) => (
                  <text key={i} x={0} y={i * 14} textAnchor="middle">
                    {line}
                  </text>
                ))}
              </g>
            )}
          />
          <Tooltip
            content={<CustomTooltip active={false} payload={[]} label={''} />}
            cursor={false}
          />
          <Bar dataKey="bev" fill={bevColor} stackId="a" />
          <Bar dataKey="phev" fill={phevColor} stackId="a" />
        </BarChart>
      </ResponsiveContainer>
      <Spacer space="beta" />
      <Grid columns={12}>
        <GridItem
          size={{ zero: 12, medium: 10, large: 8 }}
          push={{ zero: 0, medium: 1, large: 2 }}
        >
          <StyledSummary>
            {colsCount < 12 && (
              <>
                <StyledNavWrapper>
                  <StyledNavButton
                    aria-label="Previous month"
                    disabled={lastVisibleCol - colsCount === 0}
                    onClick={() => setLastVisibleCol((prev) => prev - 1)}
                    isPrev
                  >
                    <IconSVG iconSize="20" iconName="FmdsIconChevronRight" />
                  </StyledNavButton>
                  <StyledNavButton
                    aria-label="Next month"
                    disabled={lastVisibleCol === 12}
                    onClick={() => setLastVisibleCol((prev) => prev + 1)}
                  >
                    <IconSVG iconSize="20" iconName="FmdsIconChevronRight" />
                  </StyledNavButton>
                </StyledNavWrapper>
                <Spacer space="beta" />
              </>
            )}
            <div>
              <strong>{i18n.sharesInCurrentYear[locale]}</strong>
              <br />
              <span style={{ color: bevColor }}>BEV:</span> {totalBev}%,{' '}
              <span style={{ color: phevColor }}>PHEV:</span> {totalPhev}%
              <br />
              {i18n.electricVehicles[locale]}: {total}%
            </div>
          </StyledSummary>
        </GridItem>
      </Grid>
    </div>
  );
};

export type ElectricVehicleChartProps = {
  locale: string;
  data: {
    [year: string]: {
      bev: number[];
      phev: number[];
      totalBev?: number;
      totalPhev?: number;
    };
  };
};

export default ElectricVehicleChart;
